import React, { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { useProperty, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';

// ============================================================================
// Hero action rail — shared between home Hero and interior HeroSolidBG.
// ============================================================================

export const railScheduleDef: SlotGroupDefinition = {
  label: 'Rail · Schedule a Visit',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export const railResidentDef: SlotGroupDefinition = {
  label: 'Rail · Resident Access',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export const railApplyDef: SlotGroupDefinition = {
  label: 'Rail · Apply Now',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export const railTourDef: SlotGroupDefinition = {
  label: 'Rail · Virtual Tour',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export const railSelfGuidedDef: SlotGroupDefinition = {
  label: 'Rail · Self-Guided Tours',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function RailCalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="16" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 9h18M8 3v3M16 3v3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 13h2M11 13h2M15 13h2M7 16.5h2M11 16.5h2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailResidentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M13 4.5h5a1.5 1.5 0 011.5 1.5v12a1.5 1.5 0 01-1.5 1.5h-5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 8l4 4-4 4M14 12H3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailApplyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M9 11V5.5a1.5 1.5 0 013 0V11" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 11V8.5a1.5 1.5 0 013 0V11M15 11v-1a1.5 1.5 0 013 0v5.5a4.5 4.5 0 01-4.5 4.5h-2.2a4 4 0 01-3-1.35l-3-3.4a1.5 1.5 0 012.2-2l1.3 1.4V11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailTourIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8.5" cy="10" r="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 18l4.5-4.5 3 3L16 14l5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailContactIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 7.5l9 6.5 9-6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailSelfGuidedIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <circle cx="12" cy="7" r="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 20v-5.5L12 12l3.5 2.5V20" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 11.5c1.5-1 3.2-1.5 6-1.5s4.5.5 6 1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface RailLink {
  url?: string | null;
  label?: string | null;
  _target?: string | null;
}

function RailTile({
  link,
  defaultUrl,
  delayClass,
  label,
  icon,
}: {
  link: RailLink;
  defaultUrl: string;
  delayClass: string;
  label: string;
  icon: React.ReactNode;
}) {
  const href = link.url || defaultUrl;
  const target = link._target || '_self';
  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className={`hero-rail-tile ${delayClass}`}
    >
      <span className="hero-rail-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="hero-rail-label">{link.label || label}</span>
    </a>
  );
}

interface HeroActionRailProps {
  /** `full` = home hero (4 tiles). `menu-only` = interior hero (hamburger square only). */
  variant?: 'full' | 'menu-only';
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface PropertyWithTourUrl {
  virtual_tours?: VirtualTour[];
  custom_self_guided_tour_url?: string | null;
}

export function HeroActionRail({ variant = 'full' }: HeroActionRailProps) {
  const railSchedule = useSlotGroup('hero.rail_schedule', railScheduleDef);
  const railResident = useSlotGroup('hero.rail_resident', railResidentDef);
  const railApply = useSlotGroup('hero.rail_apply', railApplyDef);
  const railTour = useSlotGroup('hero.rail_tour', railTourDef);
  const railSelfGuided = useSlotGroup('hero.rail_self_guided', railSelfGuidedDef);

  const property = useProperty() as PropertyWithTourUrl | null;
  const selfGuidedUrl =
    (railSelfGuided.url || property?.custom_self_guided_tour_url || '').trim();
  const hasSelfGuided = Boolean(selfGuidedUrl);

  const hasVirtualTours = (property?.virtual_tours || []).some((t) => t.virtual_tour_url);
  const railTourLink = useMemo(
    () =>
      hasVirtualTours
        ? railTour
        : { ...railTour, url: '/contact', label: 'Contact Us' },
    [hasVirtualTours, railTour],
  );

  const menuOnly = variant === 'menu-only';

  const fourthTile = hasSelfGuided ? (
    <EditableSlotGroup groupPath="hero.rail_self_guided" definition={railSelfGuidedDef} className="hero-rail-cell">
      <RailTile
        link={{ ...railSelfGuided, url: selfGuidedUrl }}
        defaultUrl={selfGuidedUrl}
        delayClass="hero-rail-tile-4"
        label="Self-Guided Tours"
        icon={<RailSelfGuidedIcon />}
      />
    </EditableSlotGroup>
  ) : (
    <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hero-rail-cell">
      <RailTile
        link={railTourLink}
        defaultUrl={hasVirtualTours ? '/virtual-tour' : '/contact'}
        delayClass="hero-rail-tile-4"
        label={hasVirtualTours ? 'Virtual Tour' : 'Contact Us'}
        icon={hasVirtualTours ? <RailTourIcon /> : <RailContactIcon />}
      />
    </EditableSlotGroup>
  );

  return (
    <>
      <aside
        className={`hero-rail hidden lg:flex${menuOnly ? ' hero-rail--menu-only' : ''}`}
        aria-label={menuOnly ? undefined : 'Quick actions'}
        aria-hidden={menuOnly || undefined}
      >
        <div className="hero-rail-hamburger-spacer" aria-hidden="true" />

        {menuOnly ? null : (
          <>
            {/* Order: Resident → Apply → Schedule → Self-Guided (or Virtual Tour / Contact Us) */}
            <EditableSlotGroup groupPath="hero.rail_resident" definition={railResidentDef} className="hero-rail-cell">
              <RailTile
                link={railResident}
                defaultUrl="/resident-access"
                delayClass="hero-rail-tile-1"
                label="Resident Access"
                icon={<RailResidentIcon />}
              />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="hero.rail_apply" definition={railApplyDef} className="hero-rail-cell">
              <RailTile
                link={railApply}
                defaultUrl="/apply-now"
                delayClass="hero-rail-tile-2"
                label="Apply Now"
                icon={<RailApplyIcon />}
              />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="hero.rail_schedule" definition={railScheduleDef} className="hero-rail-cell">
              <RailTile
                link={railSchedule}
                defaultUrl="/schedule-a-tour"
                delayClass="hero-rail-tile-3"
                label="Schedule a Visit"
                icon={<RailCalendarIcon />}
              />
            </EditableSlotGroup>
            {fourthTile}
          </>
        )}
      </aside>

      {/* Keep unused slot wrappers mounted so schema ↔ JSX stay 1:1 when the
          fourth tile swaps between self-guided and virtual tour. */}
      {menuOnly ? null : hasSelfGuided ? (
        <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hidden" style={{ display: 'none' }}>
          <span />
        </EditableSlotGroup>
      ) : (
        <EditableSlotGroup groupPath="hero.rail_self_guided" definition={railSelfGuidedDef} className="hidden" style={{ display: 'none' }}>
          <span />
        </EditableSlotGroup>
      )}

      <style>{heroRailStyles}</style>
    </>
  );
}

export const heroRailStyles = `
  .hero-rail {
    position: absolute;
    top: var(--offer-banner-height, 0px);
    right: 0;
    bottom: 0;
    z-index: 20;
    width: var(--hero-rail-width);
    max-width: var(--hero-rail-width);
    flex-direction: column;
  }
  .hero-rail-hamburger-spacer {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    max-width: var(--hero-rail-width);
    max-height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
  }
  /* Interior heroes: hamburger square only, pinned top-right (no CTA tiles). */
  .hero-rail--menu-only {
    bottom: auto;
    height: auto;
  }
  .hero-rail--menu-only .hero-rail-hamburger-spacer {
    flex: none;
    height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
    min-height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
  }
  .hero-rail-cell {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
  }
  .hero-rail-tile {
    flex: 1 1 0;
    width: 100%;
    max-width: var(--hero-rail-width);
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    padding: 1rem 0.75rem;
    text-align: center;
    text-decoration: none;
    color: var(--vibe-color-text-on-secondary, #fff);
    background: color-mix(in srgb, var(--vibe-color-secondary) 55%, transparent);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    transition: background 0.3s ease;
    animation: heroRailIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  /* Flush against the morphing header hamburger — no divider / subpixel seam. */
  .hero-rail-hamburger-spacer + .hero-rail-cell {
    margin-top: -1px;
  }
  .hero-rail-hamburger-spacer + .hero-rail-cell .hero-rail-tile {
    border-top: none;
  }
  .hero-rail-tile:hover {
    background: color-mix(in srgb, var(--vibe-color-secondary) 90%, transparent);
  }
  .hero-rail-tile:focus-visible {
    outline: 2px solid var(--vibe-color-text-on-secondary, #fff);
    outline-offset: -3px;
  }
  .hero-rail-icon {
    display: block;
    color: var(--vibe-color-text-on-secondary, #fff);
  }
  .hero-rail-icon svg {
    width: 2rem;
    height: 2rem;
  }
  @media (min-width: 1024px) {
    .hero-rail-icon svg { width: 2.25rem; height: 2.25rem; }
  }
  .hero-rail-label {
    font-family: var(--vibe-font-body);
    font-size: 0.7rem;
    line-height: 1.25;
    letter-spacing: 0.12em;
    font-weight: 600;
    text-transform: uppercase;
  }
  @media (min-width: 1024px) {
    .hero-rail-label { font-size: 0.78rem; }
  }
  @keyframes heroRailIn {
    from { opacity: 0; transform: translateX(28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .hero-rail-tile-1 {
    animation-delay: 0.45s;
    background: color-mix(in srgb, var(--vibe-color-secondary) 68%, transparent);
  }
  .hero-rail-tile-2 {
    animation-delay: 0.6s;
    background: color-mix(in srgb, var(--vibe-color-secondary) 56%, transparent);
  }
  .hero-rail-tile-3 {
    animation-delay: 0.75s;
    background: color-mix(in srgb, var(--vibe-color-secondary) 44%, transparent);
  }
  .hero-rail-tile-4 {
    animation-delay: 0.9s;
    background: color-mix(in srgb, var(--vibe-color-secondary) 32%, transparent);
  }
  @media (prefers-reduced-motion: reduce) {
    .hero-rail-tile { animation: none; opacity: 1; transform: none; }
  }
`;
